// Trackeamento > Visão geral: escolha do destino (site próprio ou landing page), status e passo a passo.
import { useNavigate, Link } from 'react-router-dom'
import { AlertTriangle, ChevronRight, Globe, Info, LayoutTemplate, MessageCircle } from 'lucide-react'
import type { SiteMode, TrackingSetupDto } from '@impulso/shared/api/tracking'
import { errorMessage } from '../../../lib/api'
import { useCan, useCompany } from '../../../lib/company'
import { ErrorState, Loading, PageHead, Platform } from '../../../ui'
import { useToast } from '../../../ui/toast'
import { money } from '../../../features/billing/api'
import { useTracking, useTrackingStats, useUpdateTracking } from '../../../features/tracking/api'
import { Checklist, LandingAddonCard, relativeTime, TrackingStatusBadge } from '../../../features/tracking/components'
import { guideFor } from '../../../features/tracking/guides'

const MODE_TEXT: Record<SiteMode, string> = {
  own_site: 'Os anúncios levam para o seu site',
  landing_page: 'Os anúncios levam para uma página feita aqui',
  whatsapp_only: 'Você atende pelo WhatsApp e usa uma página simples para medir os cliques',
}

export default function TrackingOverview() {
  const company = useCompany()
  const q = useTracking(company.id)
  if (q.isLoading) return <Loading lines={6} />
  if (q.error) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />
  const s = q.data!
  return s.siteMode ? <Dashboard setup={s} /> : <ChooseDestination setup={s} />
}

function ChooseDestination({ setup }: { setup: TrackingSetupDto }) {
  const company = useCompany()
  const canEdit = useCan('company.edit_settings')
  const update = useUpdateTracking(company.id)
  const navigate = useNavigate()
  const toast = useToast()
  const choose = (mode: SiteMode, to: string) => {
    update.mutate({ siteMode: mode }, { onSuccess: () => navigate(`/app/${company.id}/trackeamento/${to}`), onError: (e) => toast(errorMessage(e)) })
  }
  return (
    <div className="stack" style={{ gap: 22 }}>
      <PageHead title="Medição" sub="Medir quem clicou no anúncio e virou contato é o que faz o Google e o Instagram acharem mais clientes como os seus. A gente cuida da parte técnica." />
      <div className="callout">
        <Info size={18} color="var(--primary)" style={{ flexShrink: 0, marginTop: 2 }} />
        <span className="small"><strong>Por que isso importa?</strong> Sem rastreamento, as plataformas só sabem quem clicou. Com ele, sabem quem chamou no WhatsApp, ligou ou preencheu o formulário, e passam a mostrar seu anúncio para pessoas parecidas. Na prática: mais contatos pelo mesmo dinheiro.</span>
      </div>
      <h2>Para onde você quer levar quem clica no anúncio?</h2>
      <div className="choice-grid">
        <button type="button" className="choice" disabled={!canEdit || update.isPending} onClick={() => choose('own_site', 'meu-site')}>
          <span className="ic blue"><Globe size={22} /></span>
          <h3>Já tenho um site</h3>
          <p className="small muted">WordPress, Wix, Shopify, Nuvemshop ou hospedagem própria. Mostramos o passo a passo para instalar, ou preparamos a mensagem para quem cuida do site.</p>
          <span className="choice-foot"><span>Incluído no plano</span><ChevronRight size={18} aria-hidden="true" /></span>
        </button>
        <button type="button" className="choice" disabled={!canEdit || update.isPending} onClick={() => choose('landing_page', 'landing-pages')}>
          <span className="ic purple"><LayoutTemplate size={22} /></span>
          <h3>Quero uma página pronta para anúncio</h3>
          <p className="small muted">Uma página pronta para anúncio, com botão de WhatsApp e formulário, feita no nosso editor em minutos. O rastreamento já vem instalado.</p>
          <span className="choice-foot"><span>Adicional de {money(setup.landing.price)}/mês</span><ChevronRight size={18} aria-hidden="true" /></span>
        </button>
        <button type="button" className="choice" disabled={!canEdit || update.isPending} onClick={() => choose('whatsapp_only', 'landing-pages')}>
          <span className="ic green"><MessageCircle size={22} /></span>
          <h3>Só uso WhatsApp e Instagram</h3>
          <p className="small muted">Sem site? Recomendamos uma página simples (tipo "link da bio") com o botão do WhatsApp. Assim dá para medir quem chama a partir do anúncio.</p>
          <span className="choice-foot"><span>Usa o adicional de páginas</span><ChevronRight size={18} aria-hidden="true" /></span>
        </button>
      </div>
      {!canEdit && <p className="small muted">Só quem gerencia a empresa pode escolher o destino.</p>}
    </div>
  )
}

function Dashboard({ setup }: { setup: TrackingSetupDto }) {
  const company = useCompany()
  const stats = useTrackingStats(company.id, 30)
  const canEdit = useCan('company.edit_settings')
  const update = useUpdateTracking(company.id)
  const toast = useToast()
  const t = stats.data?.totals
  const contacts = t ? t.whatsapp_click + t.phone_click + t.form_submit + t.lead + t.purchase : 0
  const rate = t && t.page_view > 0 ? contacts / t.page_view : null
  const guide = guideFor(setup.platform)
  const usesLanding = setup.siteMode !== 'own_site'
  const brokenAds = setup.adAccounts.filter((a) => a.trackingOk === false)

  return (
    <div className="stack" style={{ gap: 20 }}>
      <PageHead title="Medição" sub="Acompanhe se o seu site ou página está mandando os sinais certos para o Google e a Meta." />

      <div className="card">
        <div className="row wrap between" style={{ gap: 12, alignItems: 'flex-start' }}>
          <div className="stack" style={{ gap: 6 }}>
            <TrackingStatusBadge status={setup.status} />
            <h2 style={{ marginTop: 4 }}>{MODE_TEXT[setup.siteMode!]}</h2>
            <div className="small muted">
              {setup.siteMode === 'own_site'
                ? <>{setup.siteUrl ?? 'Endereço do site ainda não informado'}{guide ? ` · ${guide.name}` : ''} · último sinal {relativeTime(setup.lastSeenAt)}</>
                : <>{setup.landing.published} página(s) publicada(s) na conta{setup.landing.active ? ` de ${setup.landing.maxPublished}` : ''}</>}
            </div>
          </div>
          <div className="row wrap" style={{ gap: 8 }}>
            <Link className="btn primary" to={`/app/${company.id}/trackeamento/${usesLanding ? 'landing-pages' : 'meu-site'}`}>{usesLanding ? 'Minhas páginas' : 'Instalação no site'}</Link>
            {canEdit && (
              <select className="input" style={{ width: 'auto' }} aria-label="Trocar o destino dos anúncios" value={setup.siteMode ?? ''} disabled={update.isPending}
                onChange={(e) => update.mutate({ siteMode: e.target.value as SiteMode }, { onSuccess: () => toast('Destino atualizado.'), onError: (err) => toast(errorMessage(err)) })}>
                <option value="own_site">Destino: meu site</option>
                <option value="landing_page">Destino: página feita aqui</option>
                <option value="whatsapp_only">Destino: só WhatsApp</option>
              </select>
            )}
          </div>
        </div>
        <div className="divider" />
        <div className="grid g-3">
          <div className="kpi"><div className="label">Visitas (30 dias)</div><div className="value">{stats.isLoading ? '…' : (t?.page_view ?? 0).toLocaleString('pt-BR')}</div><div className="hint">Pessoas que abriram a página</div></div>
          <div className="kpi"><div className="label">Contatos (30 dias)</div><div className="value">{stats.isLoading ? '…' : contacts.toLocaleString('pt-BR')}</div><div className="hint">WhatsApp, ligações e formulários</div></div>
          <div className="kpi"><div className="label">Taxa de contato</div><div className="value">{rate == null ? '—' : `${(rate * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`}</div><div className="hint">De cada 100 visitas, quantas viram contato</div></div>
        </div>
      </div>

      {usesLanding && !setup.landing.active && <LandingAddonCard allowance={setup.landing} />}

      {brokenAds.length > 0 && (
        <div className="callout amber" role="alert">
          <AlertTriangle size={18} color="var(--amber)" style={{ flexShrink: 0, marginTop: 2 }} />
          <div className="small">
            <strong>A plataforma de anúncios avisou que não está recebendo conversões.</strong>
            <div className="row wrap mt-8" style={{ gap: 10 }}>{brokenAds.map((a) => <Platform key={a.provider + (a.name ?? '')} platform={a.provider} label={a.name ?? undefined} />)}</div>
            <div className="mt-8">Confira em <Link className="strong" to={`/app/${company.id}/trackeamento/eventos`}>Eventos e conversões</Link> se o Pixel (o “olheiro” da Meta no seu site) e a tag do Google estão ligados.</div>
          </div>
        </div>
      )}

      <div className="grid g-main">
        <Checklist items={setup.checklist} companyId={company.id} />
        <div className="card stack" style={{ gap: 12 }}>
          <h2>Como funciona</h2>
          <div className="list">
            <div className="list-item"><span className="ic purple">1</span><div className="small"><strong>Uma linha de código</strong> (ou uma página nossa) coloca a tag do Seu Impulso na página.</div></div>
            <div className="list-item"><span className="ic purple">2</span><div className="small"><strong>A tag avisa o Google e a Meta</strong> quando alguém clica no WhatsApp, liga ou envia o formulário.</div></div>
            <div className="list-item"><span className="ic purple">3</span><div className="small"><strong>As plataformas aprendem</strong> quem vira cliente e entregam o anúncio para pessoas parecidas.</div></div>
          </div>
        </div>
      </div>
    </div>
  )
}
